import type { Etat } from '../partage/api/types';

/**
 * **Ce qu'il faut se rappeler d'un geste pour pouvoir le défaire d'un clic** (ticket 29).
 *
 * « c'est fait » et « j'attends un retour » se recliquent pour s'annuler. Annuler, ce n'est
 * pas « remettre à faire » : c'est rendre l'état d'avant (à faire ou en instruction), et
 * pour une attente la date d'action d'avant. Ce fichier se rappelle ces deux valeurs, bulle
 * par bulle, et rien d'autre.
 *
 * **En mémoire, pour la session seulement** : ni en base (une colonne de plus, une migration
 * à passer sur la vraie base), ni dans le navigateur. Recharger la page l'oublie ; la fiche
 * dit alors « remettre à faire » au lieu d'« annuler », ce qui est exactement ce que fera le
 * clic. On se trompe de bouton et on se reprend dans la minute : c'est ce cas-là qu'on sert.
 *
 * Au niveau du module et pas dans un écran : la coche peut venir de la carte, de la liste
 * ou de l'inventaire, et la fiche qui l'annule peut s'ouvrir sur une autre feuille.
 *
 * **Pas réactif** : écrire ici ne redessine rien. La fiche se relit parce que chaque geste
 * qui écrit un souvenir passe aussi par une mutation, qui relit la feuille. Un souvenir
 * écrit hors d'une mutation ne se verrait qu'au rendu suivant.
 */
export interface SouvenirDuGeste {
  /** Le geste à défaire : la coche, ou l'entrée en attente. */
  geste: 'fait' | 'attente';
  etatAvant: Etat;
  /** Pour une attente seulement : la date que le geste a pu poser tout seul. */
  dateActionAvant: string | null;
  /** La coche a ouvert l'inventaire : ce qui y a été coché, jeté ou déplacé reste. */
  avecInventaire: boolean;
}

const souvenirs = new Map<string, SouvenirDuGeste>();

export function retenirLeGeste(id: string, souvenir: SouvenirDuGeste): void {
  souvenirs.set(id, souvenir);
}

export function oublierLeGeste(id: string): void {
  souvenirs.delete(id);
}

/**
 * Le souvenir, seulement s'il parle encore de la bulle telle qu'elle est. Une bulle acquise
 * dont le souvenir dit « attente » a été changée depuis par un autre chemin : il ment.
 */
export function souvenirDe(bulle: { id: string; etat: Etat }): SouvenirDuGeste | null {
  const souvenir = souvenirs.get(bulle.id);
  if (!souvenir) return null;
  const etatDuGeste: Etat = souvenir.geste === 'fait' ? 'fait' : 'en_attente_confirmation';
  return bulle.etat === etatDuGeste ? souvenir : null;
}
